// src/store/favoritesSlice.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface FavoriteItem {
  id: string;
  name: string;
  type: 'board' | 'folder';
  icon?: string;
  route?: string;
  color?: string;
}

interface FavoritesState {
  items: FavoriteItem[];
  searchQuery: string;
  isSearchActive: boolean;
}

const initialState: FavoritesState = {
  items: [
    {
      id: '0',
      name: 'Dashboard',
      type: 'board',
      icon: 'Dashboard',
      route: '/dashboard',
      color: '#0073EA'
    },
    {
      id: '1',
      name: 'Sprint Board',
      type: 'board',
      icon: 'Dashboard',
      route: '/sprint-board',
      color: '#4A90E2'
    },
    {
      id: '2',
      name: 'Teams',
      type: 'board',
      icon: 'Group',
      // No :id param - resolves to state.workspaces.currentWorkspace, so
      // this one link works regardless of which workspace is active.
      route: '/teams',
      color: '#00C875'
    },
    {
      id: '3',
      // Distinct label/icon from "Teams" above on purpose - this is the
      // workspace member directory (invite/remove members, change roles),
      // a different page from "Teams" (assignable groups of members).
      name: 'Members',
      type: 'board',
      icon: 'Person',
      route: '/team',
      color: '#FDAB3D'
    },
  ],
  searchQuery: '',
  isSearchActive: false
};

const favoritesSlice = createSlice({
  name: 'Favorites',
  initialState,
  reducers: {
    addFavorite: (state, action: PayloadAction<FavoriteItem>) => {
      state.items.push(action.payload);
    },
    removeFavorite: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter(item => item.id !== action.payload);
    },
    updateFavorite: (state, action: PayloadAction<{ id: string; updates: Partial<FavoriteItem> }>) => {
      const index = state.items.findIndex(item => item.id === action.payload.id);
      if (index !== -1) {
        state.items[index] = { ...state.items[index], ...action.payload.updates };
      }
    },
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },
    toggleSearch: (state) => {
      state.isSearchActive = !state.isSearchActive;
      if (!state.isSearchActive) {
        state.searchQuery = '';
      }
    },
    createNewFolder: (state) => {
      const newFolder: FavoriteItem = {
        id: Date.now().toString(),
        name: 'New Folder',
        type: 'folder',
        icon: 'Folder',
        color: '#6C757D'
      };
      state.items.push(newFolder);
    },
    reorderFavorites: (state, action: PayloadAction<{ fromIndex: number; toIndex: number }>) => {
      const { fromIndex, toIndex } = action.payload;
      const [movedItem] = state.items.splice(fromIndex, 1);
      state.items.splice(toIndex, 0, movedItem);
    }
  }
});

export const {
  addFavorite,
  removeFavorite,
  updateFavorite,
  setSearchQuery,
  toggleSearch,
  createNewFolder,
  reorderFavorites
} = favoritesSlice.actions;

export default favoritesSlice.reducer;